import type { TFunction } from 'i18next';
import { BookOpen, SearchX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import { useRoleText } from '../roles/role-text';
import { StateMessage } from '../ui/StateMessage';
import type { RoleCatalogEntry, useRoleCatalog } from './use-role-catalog';

type CatalogState = ReturnType<typeof useRoleCatalog>['state'];

function CatalogGroup({
  label,
  entries,
  hrefOf,
}: {
  readonly label: string;
  readonly entries: readonly RoleCatalogEntry[];
  readonly hrefOf: (entry: RoleCatalogEntry) => string;
}) {
  const { t } = useTranslation('administration');
  const { name, description } = useRoleText();
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="catalog-group">
      <h3 id={titleId} className="catalog-group-title">
        {label}
      </h3>
      <ul className="catalog-list">
        {entries.map((entry) => (
          <li key={entry.id} className="catalog-row">
            <Link to={hrefOf(entry)} className="catalog-row-name" data-role-link={entry.id}>
              {name(entry)}
            </Link>
            <span className="catalog-row-description">
              {description(entry) ?? t('roleCatalog.noDescription')}
            </span>
            <span className="catalog-row-scope">
              <span className="catalog-cell-label">{t('roleCatalog.columns.scope')}: </span>
              <span className="status-badge info">{t(`roleCatalog.scope.${entry.scope}`)}</span>
            </span>
            <span className="catalog-row-assign">
              <span className="catalog-cell-label">{t('roleCatalog.columns.assignedBy')}: </span>
              {assignedBy(t, entry.assignableBy)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** «Lo assegna»: the global administrator alone, or the tiers that may, broadest first. */
export function assignedBy(
  t: TFunction<'administration'>,
  tiers: RoleCatalogEntry['assignableBy'],
): string {
  if (tiers.length === 1 && tiers[0] === 'global') {
    return t('roleCatalog.globalOnly');
  }
  if (tiers.length === 0) {
    return t('roleCatalog.nobodyHere');
  }
  return tiers.map((tier) => t(`roleCatalog.tier.${tier}`)).join(', ');
}

/** The catalog's groups of roles, or the loading, error or no-match state that stands in. */
export function CatalogListPane({
  state,
  shown,
  groups,
  moduleLabel,
  reload,
  onClear,
  hrefOf,
}: {
  readonly state: CatalogState;
  readonly shown: readonly RoleCatalogEntry[];
  readonly groups: readonly (readonly [string, RoleCatalogEntry[]])[];
  readonly moduleLabel: (code: string, fallback: string) => string;
  readonly reload: () => void;
  readonly onClear: () => void;
  readonly hrefOf: (entry: RoleCatalogEntry) => string;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <Skeleton key={n} className="h-14 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={BookOpen}
        title={t('roleCatalog.loadError')}
        text={problemMessage(t, state.code, state.httpStatus)}
        action={
          <Button variant="outline" onClick={reload}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  if (shown.length === 0) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('roleCatalog.noResults')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('roleCatalog.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <>
      <div className="catalog-table">
        <div className="catalog-head" aria-hidden="true">
          <span>{t('roleCatalog.columns.role')}</span>
          <span>{t('roleCatalog.columns.allows')}</span>
          <span>{t('roleCatalog.columns.scope')}</span>
          <span>{t('roleCatalog.columns.assignedBy')}</span>
        </div>
        {groups.map(([code, entries]) => (
          <CatalogGroup
            key={code}
            label={moduleLabel(code, entries[0]?.module?.name ?? code)}
            entries={entries}
            hrefOf={hrefOf}
          />
        ))}
      </div>
      <p className="text-muted-foreground text-sm">
        {t('roleCatalog.results', { count: shown.length })}
      </p>
    </>
  );
}
